import { Add, DeleteOutline } from '@mui/icons-material';
import {
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from '../../../hooks/useTranslation';
import StyledFormItem from '../../../styles/StyledFormItem';
import Text from '../Text/Text';

interface HolidayListEditorProps {
  name: string;
}

type HolidayType = 'recurring' | 'one-off';

const ONE_OFF_LENGTH = 'YYYY-MM-DD'.length;

const isOneOff = (value: string): boolean => value.length === ONE_OFF_LENGTH;

const normalize = (date: Dayjs, type: HolidayType): string =>
  type === 'recurring' ? date.format('MM-DD') : date.format('YYYY-MM-DD');

const formatHolidayDate = (value: string): string => {
  if (isOneOff(value)) {
    return dayjs(value).format('MMMM D, YYYY');
  }

  // Anchor to a leap year so recurring Feb 29 doesn't roll over to March 1.
  return dayjs(`2000-${value}`).format('MMMM D');
};

const HolidayListEditor: React.FC<HolidayListEditorProps> = ({ name }) => {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const [pendingDate, setPendingDate] = useState<Dayjs | null>(null);
  const [pendingType, setPendingType] = useState<HolidayType>('recurring');
  const [hasDuplicateError, setHasDuplicateError] = useState(false);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const holidays: string[] = field.value ?? [];

        const handleAdd = () => {
          if (!pendingDate) return;

          const normalized = normalize(pendingDate, pendingType);
          if (holidays.includes(normalized)) {
            setHasDuplicateError(true);
            return;
          }

          setHasDuplicateError(false);
          field.onChange([...holidays, normalized]);
          setPendingDate(null);
        };

        const handleRemove = (index: number) => {
          field.onChange(holidays.filter((_, i) => i !== index));
        };

        return (
          <>
            <StyledFormItem>
              <Text type="label">{t('Holidays')}</Text>
              <Stack direction="row" gap={2} alignItems="center">
                <ToggleButtonGroup
                  value={pendingType}
                  exclusive
                  onChange={(_, value: HolidayType | null) => {
                    if (value) {
                      setPendingType(value);
                      setHasDuplicateError(false);
                    }
                  }}
                  aria-label={t('Holiday type')}
                  sx={{ flexShrink: 0, height: 56 }}
                >
                  <ToggleButton
                    value="recurring"
                    sx={{ whiteSpace: 'nowrap', height: '100%' }}
                  >
                    {t('Recurring')}
                  </ToggleButton>
                  <ToggleButton
                    value="one-off"
                    sx={{ whiteSpace: 'nowrap', height: '100%' }}
                  >
                    {t('One-off')}
                  </ToggleButton>
                </ToggleButtonGroup>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label={t('Date')}
                    value={pendingDate}
                    onChange={date => {
                      setPendingDate(date);
                      setHasDuplicateError(false);
                    }}
                    format={
                      pendingType === 'recurring' ? 'DD/MM' : 'DD/MM/YYYY'
                    }
                  />
                </LocalizationProvider>
                <IconButton
                  aria-label={t('Add')}
                  onClick={handleAdd}
                  disabled={!pendingDate}
                >
                  <Add />
                </IconButton>
              </Stack>
              {hasDuplicateError && (
                <Text type="body2" color="error">
                  {t('This date has already been added')}
                </Text>
              )}
            </StyledFormItem>

            <Stack>
              {holidays.map((holiday, index) => (
                <Stack
                  direction="row"
                  key={holiday}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Text type="body2">
                    {formatHolidayDate(holiday)} (
                    {isOneOff(holiday) ? t('One-off') : t('Recurring')})
                  </Text>
                  <IconButton
                    aria-label={t('Remove holiday')}
                    onClick={() => handleRemove(index)}
                  >
                    <DeleteOutline />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          </>
        );
      }}
    />
  );
};

export default HolidayListEditor;
